import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import WithdrawPaymentModals from "../models/WithdrawPaymentModals";
import WithdrawalTicketDetailsModal from "../models/WithdrawalTicketDetailsModal";
import ReferralDataCommissions from "../models/ReferralDataCommissions";
import axiosConfig from "../../axios/axiosConfig";

const headCellSx = {
  fontWeight: 700,
  fontSize: "12px",
  color: "#fff",
  background: "#04343a",
  borderBottom: "none",
  padding: "10px 14px",
  whiteSpace: "nowrap",
};

const statusChip = (status) => {
  const isCompleted = status === "completed";
  const isPending = status === "pending";
  return (
    <Chip
      label={status || "—"}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "11px",
        textTransform: "capitalize",
        bgcolor: isCompleted ? "#dcfce7" : isPending ? "#fef9c3" : "#f1f5f9",
        color: isCompleted ? "#15803d" : isPending ? "#854d0e" : "#64748b",
      }}
    />
  );
};

const UserWithdrawal = () => {
  const [withdrawal, setWithdrawal] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [paymentModalData, setPaymentModalData] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [currencyModalUserId, setCurrencyModalUserId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const fetchWithdrawal = async () => {
    setLoading(true);
    try {
      const response = await axiosConfig({ method: "get", url: "/tnibroadmin/get-all-withdrawl-request/" });
      setWithdrawal(response?.data?.response || []);
    } catch (error) {
      toast.error(error?.response?.data?.response || error?.message || "Failed to fetch withdrawal data.");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      const res = await axiosConfig({ method: "put", url: `/tnibroadmin/approve-all-withdrawl-request/${id}/` });
      toast.success(res?.data?.response || "Withdrawal approved!");
      fetchWithdrawal();
    } catch {
      toast.error("Failed to approve withdrawal");
    } finally {
      setActionLoading(null);
      handleCloseMenu();
    }
  };

  const handleMenuClick = (event, row) => { setMenuAnchor(event.currentTarget); setSelectedRow(row); };
  const handleCloseMenu = () => { setMenuAnchor(null); setSelectedRow(null); };
  const handlePaymentClick = () => { setPaymentModalData({ id: selectedRow?.id }); handleCloseMenu(); };

  useEffect(() => { fetchWithdrawal(); }, []);

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 28px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <AccountBalanceWalletIcon sx={{ color: "#fff", fontSize: 32 }} />
        <Box>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>
            User Withdrawals
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}>
            View and manage withdrawal requests
          </Typography>
        </Box>
      </Box>

      {/* Table Card */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ background: "#04343a", px: "20px", py: "12px", display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "15px" }}>
            Withdrawal Requests
          </Typography>
          {!loading && (
            <Chip
              label={`Total: ${withdrawal.length}`}
              size="small"
              sx={{ bgcolor: "rgba(1,101,81,0.3)", color: "#fff", fontWeight: 600, fontSize: "12px" }}
            />
          )}
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress sx={{ color: "#016551" }} />
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ borderTop: "1px solid #eef2f7", overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                minWidth: 800,
                "& .MuiTableCell-root": {
                  padding: "9px 14px",
                  fontSize: "12px",
                  borderBottom: "1px solid #f0f4f8",
                  whiteSpace: "nowrap",
                },
                "& .MuiTableBody-root .MuiTableRow-root:hover": { background: "#f0faf5" },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 45 }}>S.No.</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 60 }}>ID</TableCell>
                  <TableCell sx={{ ...headCellSx, minWidth: 130 }}>Username</TableCell>
                  <TableCell sx={{ ...headCellSx, minWidth: 130 }}>Ticket ID</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 90 }}>Amount</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 100 }}>Status</TableCell>
                  <TableCell sx={{ ...headCellSx, minWidth: 140 }}>Referral IDs</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 140 }}>Created At</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 90 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {withdrawal.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5, color: "#94a3b8", fontSize: "13px" }}>
                      No withdrawal requests found.
                    </TableCell>
                  </TableRow>
                ) : (
                  withdrawal.map((row, index) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ color: "#94a3b8" }}>{index + 1}</TableCell>
                      <TableCell sx={{ color: "#64748b" }}>{row.id}</TableCell>
                      <TableCell sx={{ color: "#1e293b", fontWeight: 600 }}>{row.usernmae}</TableCell>
                      <TableCell>
                        <Typography
                          sx={{ color: "#016551", fontWeight: 600, cursor: "pointer", textDecoration: "underline", fontSize: "12px" }}
                          onClick={() => setSelectedTicket(row.ticket_id)}
                        >
                          {row.ticket_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700, fontSize: "12px", color: "#016551" }}>
                          ₹{row.amount}
                        </Typography>
                      </TableCell>
                      <TableCell>{statusChip(row.status)}</TableCell>
                      <TableCell sx={{ color: "#64748b" }}>{row.referral_id.join(", ")}</TableCell>
                      <TableCell sx={{ color: "#64748b" }}>{new Date(row.created_at).toLocaleString()}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="More actions" arrow>
                            <IconButton size="small" onClick={(e) => handleMenuClick(e, row)} sx={{ color: "#04343a", "&:hover": { bgcolor: "#e6f4f1" } }}>
                              <MoreHorizIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Referral Commissions" arrow>
                            <IconButton size="small" onClick={() => setCurrencyModalUserId(row.user_id)} sx={{ color: "#016551", "&:hover": { bgcolor: "#e6f4f1" } }}>
                              <CurrencyRupeeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Dropdown Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <MenuItem
          onClick={() => handleApprove(selectedRow?.id)}
          disabled={actionLoading === selectedRow?.id || selectedRow?.status === "completed"}
        >
          {actionLoading === selectedRow?.id ? "Approving..." : "Approve"}
        </MenuItem>
        <MenuItem onClick={handlePaymentClick}>Payment</MenuItem>
      </Menu>

      {paymentModalData && (
        <WithdrawPaymentModals
          open={Boolean(paymentModalData)}
          onClose={() => setPaymentModalData(null)}
          withdrawalId={paymentModalData.id}
          onSuccess={() => { setPaymentModalData(null); fetchWithdrawal(); }}
        />
      )}
      {selectedTicket && (
        <WithdrawalTicketDetailsModal
          open={Boolean(selectedTicket)}
          ticketId={selectedTicket}
          onClose={() => setSelectedTicket(null)}
        />
      )}
      {currencyModalUserId && (
        <ReferralDataCommissions
          open={Boolean(currencyModalUserId)}
          userId={currencyModalUserId}
          onClose={() => setCurrencyModalUserId(null)}
        />
      )}
    </Box>
  );
};

export default UserWithdrawal;
